import React, { useEffect, useMemo, useRef, useState } from 'react';
// @ts-ignore - Remotion型定義の互換性問題の暫定対応（既存remotionコンポジション踏襲）
import { Player } from '@remotion/player';
import { SongAdProject, SongAdScene } from '../../services/api/songAdService';
import { TaskyButton, TaskyEmptyState, TaskyAlert, TaskyCard, TaskySwitch, TaskyInput } from '../tasky';
import {
  SongAdComposition,
  deriveCompositionScenes,
  deriveCtaCard,
  getSongAdDimensions,
  getSongAdDurationInFrames,
  SONG_AD_FPS,
  stabilizeCompositionMedia,
} from '../../remotion/song-ad';
import { cn } from '../../lib/utils';
import { Film, Download, SlidersHorizontal, PenSquare } from 'lucide-react';

/**
 * ステップ⑤: 完成プレビュー（spec §13）。
 *
 * @remotion/player でインライン再生する（VideoAdsPage.tsx の Player 利用イディオム踏襲: ref +
 * durationInFrames/compositionWidth/compositionHeight を固定し、変わるときだけ key でremount）。
 * 「このまま書き出す」「詳細編集する」の2導線のみ（§17.2動画全体AI修正はスコープ外なのでボタンを出さない）。
 *
 * CTA（締めカット）カードは常に自動挿入される実装（backend既定 cta_enabled=true）だが、
 * ON/OFFと文言をユーザーが制御するUIがここに無かったため追加する（v4 F12）。
 * `deriveCtaCard` は `project.cta.enabled`/`project.advanced_settings.cta` を見るだけなので、
 * PATCH後にプロジェクトを再取得すれば自動的にプレビューへ反映される（ロジック変更不要）。
 *
 * v6 F2（o）: ロゴのプレビュー文言を実挙動に合わせて修正。`project.cta.logo_url` は
 * `advanced_settings.logo_image_key`（①情報登録で明示指定）→ 商品のロゴ画像（brand_logo）→
 * 選択画像の1枚目、の順で解決されるため、「商品のロゴ画像が使われます」と決め打ちしない。
 * ロゴの変更導線は①情報登録タブへ委ねる（`onEditSettings`）。
 *
 * v5 k: CTA文言のPATCH等で project が再取得されるたびに presigned URL が新しい署名で再発行され、
 * `<Audio>/<Video>/<Img>` がリロードされて再生位置が先頭へ戻る問題があったため、
 * `stabilizeCompositionMedia()` でURL文字列値を安定化してから Player へ渡す（EditorStepと共通）。
 */

const CTA_TEXT_DEBOUNCE_MS = 1000;

interface PreviewStepProps {
  project: SongAdProject;
  scenes: SongAdScene[];
  onProceedToExport: () => void;
  onProceedToEditor: () => void;
  /** CTAカードのON/OFFをPATCHする（cta_enabled） */
  onUpdateCtaEnabled: (enabled: boolean) => Promise<void>;
  /** CTA文言をPATCHする（advanced_settings.cta。空文字は商品名フォールバックのためnullで送る） */
  onUpdateCtaText: (text: string) => Promise<void>;
  /** ①情報登録タブへの変更導線（v6 F2/o）。ロゴの選択・変更はそちらで行う */
  onEditSettings?: () => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  project,
  scenes,
  onProceedToExport,
  onProceedToEditor,
  onUpdateCtaEnabled,
  onUpdateCtaText,
  onEditSettings,
}) => {
  const hasSong = !!project.song_url;
  const missingStoryboards = scenes.filter((s) => !s.storyboard.image);
  const canPreview = hasSong && scenes.length > 0 && missingStoryboards.length === 0;

  // 実測の楽曲尺があればそれを正にする（backendのSongAdRenderPlannerも song_duration_sec を使う。
  // 未生成時は選択中の target_seconds でフォールバック）
  const totalSeconds = project.song_duration_sec ?? project.target_seconds;
  const compositionScenes = useMemo(
    () => deriveCompositionScenes(scenes, project.timeline, totalSeconds),
    [scenes, project.timeline, totalSeconds]
  );
  // v4 F9: CTAカード分だけ末尾シーンを縮める（総尺は変えない。書き出しと一致させる）
  const { scenes: renderScenes, cta } = useMemo(
    () => deriveCtaCard(compositionScenes, project, totalSeconds),
    [compositionScenes, project, totalSeconds]
  );
  const captions = project.timeline?.captions ?? [];
  const dimensions = getSongAdDimensions(project.aspect_ratio);
  const durationInFrames = getSongAdDurationInFrames(totalSeconds);
  // v5 k: EditorStepと同じURL安定化（advisor-notes.md (2)）。CTA文言の1秒debounce保存等でも
  // project が差し替わるため、ここでも presigned URL の再発行によるメディア再ロードを防ぐ
  const stableMedia = useMemo(
    () => stabilizeCompositionMedia(renderScenes, cta, project.song_url),
    [renderScenes, cta, project.song_url]
  );

  // ON/OFFは project.cta.enabled を優先し、無ければ互換の cta_enabled にフォールバックする
  // （deriveCtaCard と同じ優先規則。§14 EditorStepのプレビューとも一致させる）
  const ctaEnabled = project.cta?.enabled ?? project.cta_enabled;
  const ctaLogoUrl = project.cta?.logo_url ?? null;
  const savedCtaText = project.advanced_settings?.cta ?? '';

  const [ctaTextDraft, setCtaTextDraft] = useState(savedCtaText);
  const [isTogglingCta, setIsTogglingCta] = useState(false);
  const ctaTextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // サーバー側の値（他タブでの変更・ポーリング等）が変わったらドラフトを同期する
  useEffect(() => {
    setCtaTextDraft(savedCtaText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCtaText]);

  useEffect(
    () => () => {
      if (ctaTextTimerRef.current) clearTimeout(ctaTextTimerRef.current);
    },
    []
  );

  const handleToggleCta = async (checked: boolean) => {
    setIsTogglingCta(true);
    try {
      await onUpdateCtaEnabled(checked);
    } finally {
      setIsTogglingCta(false);
    }
  };

  const commitCtaText = (text: string) => {
    if (ctaTextTimerRef.current) {
      clearTimeout(ctaTextTimerRef.current);
      ctaTextTimerRef.current = null;
    }
    if (text === savedCtaText) return;
    onUpdateCtaText(text);
  };

  // 入力途中で毎回PATCHしない。1秒debounceし、フォーカスが外れたらすぐ確定させる
  const handleCtaTextChange = (text: string) => {
    setCtaTextDraft(text);
    if (ctaTextTimerRef.current) clearTimeout(ctaTextTimerRef.current);
    ctaTextTimerRef.current = setTimeout(() => commitCtaText(text), CTA_TEXT_DEBOUNCE_MS);
  };

  if (!canPreview) {
    return (
      <TaskyEmptyState
        icon={<Film />}
        title="先に楽曲とストーリーボードを完成させてください"
        description="「② 楽曲」で曲を生成し、「③ ストーリーボード」で全シーンの絵コンテを揃えるとプレビューできます"
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="text-center mb-5">
        <h2 className="text-lg font-semibold text-dark">完成動画のプレビュー</h2>
      </div>

      {!captions.length && (
        <TaskyAlert tone="info" className="mb-4">
          歌詞テロップが未取得です。「② 楽曲」で「歌詞タイミングを取得」を実行してください。
        </TaskyAlert>
      )}

      {/* v8: 「このまま書き出す」「詳細編集する」をプレビュー映像の右側に配置する。
          狭い画面（sm未満）ではこれまで通り縦積みに落とす。
          注意: このdivをflex行の子にした際、旧実装のように maxWidth だけで width を
          指定しないと、flexアイテムのcross/main-axisサイズ計算で幅が0に収束し、
          aspect-ratioで高さも0になって非表示になる（EditorStepで実際に踏んだのと同じ罠。
          height/widthのどちらかを"定義された値"にする必要がある）。ここでは width を
          明示の数値にして回避する */}
      <div className="bg-[#F4F3EF] rounded-2xl p-5 mb-5 flex flex-col items-center gap-5 sm:flex-row sm:items-center">
        <div
          className="bg-black rounded-xl overflow-hidden mx-auto shadow shrink-0"
          style={{
            aspectRatio: `${dimensions.width} / ${dimensions.height}`,
            width: project.aspect_ratio === '9:16' ? 360 : project.aspect_ratio === '1:1' ? 480 : 560,
            maxWidth: '100%',
          }}
        >
          <Player
            key={`${project.aspect_ratio}-${durationInFrames}-${compositionScenes.length}`}
            component={SongAdComposition}
            inputProps={{
              scenes: stableMedia.scenes,
              captions,
              audioUrl: stableMedia.audioUrl,
              cta: stableMedia.cta,
            }}
            durationInFrames={durationInFrames}
            fps={SONG_AD_FPS}
            compositionWidth={dimensions.width}
            compositionHeight={dimensions.height}
            style={{ width: '100%', height: '100%' }}
            controls
            loop
          />
        </div>

        <div className="flex flex-col items-center gap-3 sm:flex-1 sm:items-start">
          <TaskyButton onClick={onProceedToExport} className="w-full sm:w-auto">
            <Download className="w-4 h-4" />
            このまま書き出す
          </TaskyButton>
          <TaskyButton variant="outline" onClick={onProceedToEditor} className="w-full sm:w-auto">
            <SlidersHorizontal className="w-4 h-4" />
            詳細編集する
          </TaskyButton>
          <p className="text-center text-[11px] text-gray-400 sm:text-left">
            タイムライン編集は「詳細編集」からいつでも。編集内容は書き出し（合成）に反映されます。
          </p>
        </div>
      </div>

      <TaskyCard variant="warm" className="mb-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-dark">CTA（締めカット）</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              動画の最後に約2.5秒のカットとして入ります（動画全体の長さは変わりません）
            </p>
          </div>
          <TaskySwitch
            checked={ctaEnabled}
            disabled={isTogglingCta}
            onChange={(e) => handleToggleCta(e.target.checked)}
            label="動画の最後にCTAを入れる"
          />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-[1fr_auto] sm:items-start">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5" htmlFor="song-ad-cta-text">
              CTA文言
            </label>
            <TaskyInput
              id="song-ad-cta-text"
              value={ctaTextDraft}
              disabled={!ctaEnabled}
              maxLength={100}
              placeholder="例: 今すぐチェック"
              onChange={(e) => handleCtaTextChange(e.target.value)}
              onBlur={() => commitCtaText(ctaTextDraft)}
            />
            <p className="text-[11px] text-gray-400 mt-1">未入力のときは商品名が使われます</p>
          </div>

          <div className={cn('flex flex-col gap-1', !ctaEnabled && 'opacity-50')}>
            <span className="text-xs font-medium text-gray-600">ロゴ</span>
            {ctaLogoUrl ? (
              <div className="flex items-center gap-2">
                <img
                  src={ctaLogoUrl}
                  alt="CTAロゴ"
                  className="w-10 h-10 rounded-md object-contain bg-white border border-warm-border shrink-0"
                />
                {/* v6 F2: 未指定の場合は選択画像の1枚目が使われることもあるため、
                    「商品のロゴ画像が使われます」と決め打ちしない（実挙動と食い違っていた） */}
                <span className="text-[11px] text-gray-400 max-w-[9rem]">
                  ①情報登録で指定したロゴ、未指定時は商品のロゴ画像→選択画像の1枚目が使われます
                </span>
              </div>
            ) : (
              <span className="text-[11px] text-gray-400 max-w-[9rem]">
                ロゴ未登録（①情報登録でロゴを指定するか、商品にロゴ画像を登録すると自動で入ります）
              </span>
            )}
            {onEditSettings && (
              <button
                type="button"
                onClick={onEditSettings}
                className="flex items-center gap-1 text-[11px] text-primary hover:underline mt-0.5 self-start"
              >
                <PenSquare className="w-3 h-3" />
                ロゴを変更
              </button>
            )}
          </div>
        </div>
      </TaskyCard>
    </div>
  );
};

export default PreviewStep;
